import { Stethoscope, Clock4, FileText, Briefcase, GraduationCap, Heart, Baby, Gavel, Droplets, Palmtree } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export const PLEA_TYPE_OPTIONS = [
  'Esquecimento',
  'Acidente de Trabalho',
  'Alistamento Militar',
  'Atestado de Óbito',
  'Atestado Médico',
  'Casamento',
  'Curso',
  'Comparecimento em Juízo',
  'Doação de sangue',
  'Licença maternidade/paternidade',
  'Paralisação de serviço pelo empregador',
  'Solicitação de folga',
  'Solicitação de férias',
  'Vestibular',
  'Outros',
] as const;

interface PleaTypeStyle {
  icon: LucideIcon;
  bg: string;
  border: string;
  color: string;
}

const TYPE_STYLES: Record<string, PleaTypeStyle> = {
  'Atestado Médico': { icon: Stethoscope, bg: 'bg-blue-50', border: 'border-blue-100', color: 'text-blue-600' },
  'Esquecimento': { icon: Clock4, bg: 'bg-slate-50', border: 'border-slate-100', color: 'text-slate-500' },
  'Acidente de Trabalho': { icon: Heart, bg: 'bg-red-50', border: 'border-red-100', color: 'text-red-500' },
  'Casamento': { icon: Heart, bg: 'bg-pink-50', border: 'border-pink-100', color: 'text-pink-500' },
  'Curso': { icon: GraduationCap, bg: 'bg-indigo-50', border: 'border-indigo-100', color: 'text-indigo-500' },
  'Vestibular': { icon: GraduationCap, bg: 'bg-indigo-50', border: 'border-indigo-100', color: 'text-indigo-500' },
  'Comparecimento em Juízo': { icon: Gavel, bg: 'bg-amber-50', border: 'border-amber-100', color: 'text-amber-600' },
  'Doação de sangue': { icon: Droplets, bg: 'bg-red-50', border: 'border-red-100', color: 'text-red-500' },
  'Licença maternidade/paternidade': { icon: Baby, bg: 'bg-purple-50', border: 'border-purple-100', color: 'text-purple-500' },
  'Solicitação de folga': { icon: Palmtree, bg: 'bg-green-50', border: 'border-green-100', color: 'text-green-500' },
  'Solicitação de férias': { icon: Palmtree, bg: 'bg-green-50', border: 'border-green-100', color: 'text-green-500' },
  'Paralisação de serviço pelo empregador': { icon: Briefcase, bg: 'bg-slate-50', border: 'border-slate-100', color: 'text-slate-500' },
};

const DEFAULT_STYLE: PleaTypeStyle = { icon: FileText, bg: 'bg-slate-50', border: 'border-slate-100', color: 'text-slate-500' };

export function getPleaTypeStyle(type: string): PleaTypeStyle {
  return TYPE_STYLES[type] ?? DEFAULT_STYLE;
}

interface StatusStyle {
  label: string;
  bg: string;
  text: string;
}

const STATUS_STYLES: Record<string, StatusStyle> = {
  pending: { label: 'Análise', bg: 'bg-amber-100', text: 'text-amber-800' },
  accepted: { label: 'Aprovado', bg: 'bg-green-100', text: 'text-green-800' },
  approved: { label: 'Aprovado', bg: 'bg-green-100', text: 'text-green-800' },
  rejected: { label: 'Rejeitado', bg: 'bg-red-100', text: 'text-red-800' },
};

const DEFAULT_STATUS: StatusStyle = { label: 'Pendente', bg: 'bg-amber-100', text: 'text-amber-800' };

export function getPleaStatusStyle(status: string | undefined): StatusStyle {
  if (!status) return DEFAULT_STATUS;
  return STATUS_STYLES[status.toLowerCase()] ?? DEFAULT_STATUS;
}

const PENDING_STATUS = 'pending';

/**
 * Tells whether the manager has already decided on a plea, which locks it against edits and deletion.
 * Any non-empty status other than pending (case-insensitive) is a decision.
 */
export function isPleaDecided(status: string | undefined): boolean {
  return !!status && status.toLowerCase() !== PENDING_STATUS;
}

export const STATUS_FILTERS = [
  { key: 'all', label: 'Todos' },
  { key: 'pending', label: 'Pendentes' },
  { key: 'accepted', label: 'Aprovados' },
  { key: 'rejected', label: 'Rejeitados' },
] as const;
